import RestaurantService from "@/services/RestaurantService";

export const state = {
  has_data: false,
  revenue_already_inserted: 0.0,
  products: null,
  uploading: false,
  upload_task: null,
  available_months: null,
  available_years: null,
  sales_report_list_labels: null,
  sales_report_start_date: null,
  sales_report_end_date: null,
  sales_report_start_date_to_compare: null,
  sales_report_end_date_to_compare: null,

  // status
  sales_report_data_status: 0,
  sales_report_list_status: 0,

  // dataset
  sales_report_data: null,
  sales_report_list: null,

  // ui
  sales_period: null,
  sales_period_month: null,
  sales_period_year: null,
  sales_vs: null,
  sales_hotel_vs: null,
  sales_hotel_vs_name: null
};

export const mutations = {
  GET_DATASET(state, { dataset, data }) {
    state[dataset] = data;
  },
  SET_DATASET_STATUS(state, { dataset, status }) {
    state[dataset + "_status"] = status;
  },
  SET_HAS_DATA(state, hasData) {
    state.has_data = hasData;
  },
  SET_REVENUE_AMOUNT(state, amount) {
    state.revenue_already_inserted = amount;
  },
  SET_PRODUCTS(state, products) {
    state.products = products;
  },
  SET_UPLOADING(state, isUploading) {
    state.uploading = isUploading;
  },
  SET_UPLOAD_TASK(state, task) {
    state.upload_task = task;
  },
  UPDATE_UPLOAD_TASK(state, updates) {
    state.upload_task = updates;
  },
  SET_SALES_REPORT_DATA(state, data) {
    state.sales_report_data = data;
  },
  SET_SALES_REPORT_LIST(state, list) {
    state.sales_report_list = list;
  },
  SET_SALES_REPORT_LIST_LABELS(state, labels) {
    state.sales_report_list_labels = labels;
  },
  SET_AVAILABLE_MONTHS(state, months) {
    state.available_months = months;
  },
  SET_AVAILABLE_YEARS(state, years) {
    state.available_years = years;
  },
  SET_SALES_PERIOD(state, period) {
    state.sales_period = period;
  },
  SET_SALES_PERIOD_MONTH(state, month) {
    state.sales_period_month = month;
  },
  SET_SALES_PERIOD_YEAR(state, year) {
    state.sales_period_year = year;
  },
  SET_SALES_VS(state, vs) {
    state.sales_vs = vs;
  },
  SET_SALES_HOTEL_VS(state, hotel_vs) {
    state.sales_hotel_vs = hotel_vs;
  },
  SET_SALES_HOTEL_VS_NAME(state, hotel_vs_name) {
    state.sales_hotel_vs_name = hotel_vs_name;
  },
  SET_SALES_REPORT_START_DATE(state, start_date) {
    state.sales_report_start_date = start_date;
  },
  SET_SALES_REPORT_END_DATE(state, end_date) {
    state.sales_report_end_date = end_date;
  },
  SET_SALES_REPORT_START_DATE_TO_COMPARE(state, start_date) {
    state.sales_report_start_date_to_compare = start_date;
  },
  SET_SALES_REPORT_END_DATE_TO_COMPARE(state, end_date) {
    state.sales_report_end_date_to_compare = end_date;
  }
};

export const actions = {
  getRestaurantProducts({ commit }, params) {
    return RestaurantService.getRestaurantProducts(params).then(response => {
      if (response.error) throw new Error(response.error);
      commit("SET_PRODUCTS", response.data);
      return response.data;
    });
  },
  loadRestaurantRevenueDate({ commit }, params) {
    return RestaurantService.loadRevenueDate(params).then(response => {
      commit("SET_HAS_DATA", response.data.has_data === true);
      if (response.data.has_data) {
        commit("SET_REVENUE_AMOUNT", response.data.data);
      }
      return response.data;
    });
  },
  saveRestaurantRevenueDay({ commit }, params) {
    return RestaurantService.saveRevenueDay(params).then(response => {
      return response.data;
    });
  },
  deleteRestaurantRevenueDay({ commit }, params) {
    return RestaurantService.removeRevenueDay(params).then(response => {
      return response.data;
    });
  },
  uploadRestaurantDataFile({ commit }, params) {
    return RestaurantService.uploadRestaurantDataFile(params.formData).then(
      response => {
        commit("SET_UPLOAD_TASK", response.data);
        return response.data;
      }
    );
  },
  getRestaurantDataFileStatus({ commit }, params) {
    return RestaurantService.getRestaurantDataFileStatus(params).then(
      response => {
        commit("UPDATE_UPLOAD_TASK", response.data);
        return response.data;
      }
    );
  },
  fetchRestaurantSalesReportData({ commit, state, rootGetters }, p) {
    const dataset = "sales_report_data";
    commit("SET_DATASET_STATUS", { dataset: dataset, status: 1 });

    // Required params
    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id) {
      return null;
    }

    let params = {
      hotel_id: rootGetters.current_hotel.id,
      vs: state.sales_vs,
      hotel_vs: state.sales_hotel_vs ? state.sales_hotel_vs : 0
    };

    const handleResponse = response => {
      commit("GET_DATASET", { dataset, data: response.data });
      commit("SET_DATASET_STATUS", { dataset, status: 2 });
      commit(
        "SET_SALES_REPORT_START_DATE",
        response.data.report.revenuerestaurant_start_date
      );
      commit(
        "SET_SALES_REPORT_END_DATE",
        response.data.report.revenuerestaurant_end_date
      );
      commit(
        "SET_SALES_REPORT_START_DATE_TO_COMPARE",
        response.data.data_to_compare.revenuerestaurant_start_date
      );
      commit(
        "SET_SALES_REPORT_END_DATE_TO_COMPARE",
        response.data.data_to_compare.revenuerestaurant_end_date
      );
    };

    if (state.sales_period === "current_month") {
      return RestaurantService.getCurrentMonthReport(params).then(
        handleResponse
      );
    } else if (state.sales_period === "current_year") {
      return RestaurantService.getCurrentYearReport(params).then(
        handleResponse
      );
    } else if (
      state.sales_period === "past" &&
      state.sales_period_month !== null &&
      state.sales_period_year !== null
    ) {
      params.month = state.sales_period_month;
      params.year = state.sales_period_year;
      return RestaurantService.getPastMonthReport(params).then(handleResponse);
    } else if (
      state.sales_period === "past" &&
      state.sales_period_month === null &&
      state.sales_period_year !== null
    ) {
      params.year = state.sales_period_year;
      return RestaurantService.getPastYearReport(params).then(handleResponse);
    }
  },
  fetchRestaurantSalesReportList({ commit, state, rootGetters }, p) {
    const dataset = "sales_report_list";
    commit("SET_DATASET_STATUS", { dataset: dataset, status: 1 });

    if (!rootGetters.current_hotel || !rootGetters.current_hotel.id) {
      return null;
    }

    let params = {
      hotel_id: rootGetters.current_hotel.id,
      vs: state.sales_vs,
      hotel_vs: state.sales_hotel_vs ? state.sales_hotel_vs : 0
    };

    const handleResponse = response => {
      commit("GET_DATASET", { dataset: dataset, data: response.data });
      commit("SET_DATASET_STATUS", { dataset: dataset, status: 2 });
    };

    if (state.sales_period === "current_month") {
      return RestaurantService.getCurrentMonthList(params).then(handleResponse);
    } else if (state.sales_period === "current_year") {
      return RestaurantService.getCurrentYearList(params).then(handleResponse);
    } else if (
      state.sales_period === "past" &&
      state.sales_period_month !== null &&
      state.sales_period_year !== null
    ) {
      params.month = state.sales_period_month;
      params.year = state.sales_period_year;
      return RestaurantService.getPastMonthList(params).then(handleResponse);
    } else if (
      state.sales_period === "past" &&
      state.sales_period_month === null &&
      state.sales_period_year !== null
    ) {
      params.year = state.sales_period_year;
      return RestaurantService.getPastYearList(params).then(handleResponse);
    }
  },
  getRestaurantAvailableMonths({ commit }, params) {
    return RestaurantService.getAvailableMonths(params).then(response => {
      commit("SET_AVAILABLE_MONTHS", response.data);
      return response.data;
    });
  },
  getRestaurantAvailableYears({ commit }, params) {
    return RestaurantService.getAvailableYears(params).then(response => {
      commit("SET_AVAILABLE_YEARS", response.data);
      return response.data;
    });
  },
  setSalesReportListLabels({ commit }, labels) {
    commit("SET_SALES_REPORT_LIST_LABELS", labels);
  }
};

export const getters = {
  restaurant_sales_chart_data: state => {
    let info = state.sales_report_list;
    if (info) {
      let data = {
        labels: {},
        series: {},
        colors: ["#5489d0", "#edae10"],
        vs: state.sales_vs,
        period: state.sales_period,
        hotel_vs_name: state.sales_hotel_vs_name
      };
      data.labels = info.daily_list.map(item => item.date_original);
      data.series.period = info.daily_list.map(item =>
        item.total_sale && item.total_sale.gross ? item.total_sale.gross : 0
      );
      data.series.vs = info.daily_list_to_compare.map(item =>
        item.total_sale && item.total_sale.gross ? item.total_sale.gross : 0
      );
      return data;
    }
    return {};
  }
};
